<template>
  <div class="carousel__external" tabindex="-1">
    <button
      class="carousel__arrow carousel__leftArrow"
      aria-label="Previous"
      :style="{ backgroundColor: backgroundColorArrow }"
      @click="prevSlide"
      v-if="hasArrows"
    >
      <svg
        class="carousel__arrowImage"
        width="15"
        height="24"
        viewBox="0 0 15 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          :fill="colorArrow"
          d="M12.0003 0L14.8203 2.82L5.66031 12L14.8203 21.18L12.0003 24L0.000312805 12L12.0003 0Z"
        />
      </svg>
    </button>

    <button
      class="carousel__arrow carousel__rightArrow"
      aria-label="Next"
      :style="{ backgroundColor: backgroundColorArrow }"
      @click="nextSlide"
      v-if="hasArrows"
    >
      <svg
        class="carousel__arrowImage"
        width="15"
        height="24"
        viewBox="0 0 15 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          :fill="colorArrow"
          d="M2.99969 0L0.179688 2.82L9.33969 12L0.179688 21.18L2.99969 24L14.9997 12L2.99969 0Z"
        />
      </svg>
    </button>

    <div class="carousel">
      <div class="carousel__indicators" v-if="hasIndicators">
        <button
          v-for="(title, index) in titlesArray"
          :key="index"
          class="carousel__indicator"
          :style="
            currentIndex === index
              ? { backgroundColor: activeIndicatorColor, borderColor: activeIndicatorBorderColor }
              : { backgroundColor: indicatorColor, borderColor: indicatorBorderColor }
          "
          :class="{ carousel__active: currentIndex === index }"
          :aria-label="title"
          :aria-current="currentIndex === index"
          tabindex="0"
          @keydown="goToSlide(index)"
          @click="goToSlide(index)"
        />
      </div>
      <div
        ref="carouselContainer"
        class="carousel__container"
        :style="containerStyle"
      >
        <slot />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'c-carousel',
  props: {
    autoplay: {
      type: Boolean,
      default: false,
    },
    autoplaySpeed: {
      type: Number,
      default: 10000,
    },
    hasArrows: {
      type: Boolean,
      default: true,
    },
    backgroundColorArrow: {
      type: String,
      default: '#9ddda9',
    },
    colorArrow: {
      type: String,
      default: '#FFFFFF',
    },
    hasIndicators: {
      type: Boolean,
      default: true,
    },
    indicatorColor: {
      type: String,
      default: 'transparent',
    },
    indicatorBorderColor: {
      type: String,
      default: '#FFFFFF',
    },
    activeIndicatorColor: {
      type: String,
      default: '#9ddda9',
    },
    activeIndicatorBorderColor: {
      type: String,
      default: '#9ddda9',
    }
  },
  data() {
    return {
      currentIndex: 0,
      innerDivCount: 0,
      containerTranslate: '',
      titlesArray: [],
      autoplayInterval: null,
    }
  },
  computed: {
    containerStyle() {
      return {
        width: `${this.innerDivCount * 100}%`,
        transform: `translateX(${this.containerTranslate})`,
      }
    },
    validatedAutoplaySpeed() {
      return this.autoplaySpeed === null || this.autoplaySpeed === undefined || this.autoplaySpeed === '' || this.autoplaySpeed === 0
      ? 10000
      : this.autoplaySpeed
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.countChildren()
      this.collectTitles()
      this.updateContainerTranslate()
      if (this.autoplay) {
        this.startAutoplay()
      }
    })
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
    this.stopAutoplay()
  },
  methods: {
    startAutoplay() {
      this.autoplayInterval = setInterval(() => {
        this.nextSlideInternal()
      }, this.validatedAutoplaySpeed)
    },
    stopAutoplay() {
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval)
        this.autoplayInterval = null
      }
    },
    nextSlideInternal() {
      if (this.currentIndex < this.innerDivCount - 1) {
        this.currentIndex++
      } else {
        this.currentIndex = 0
      }
      this.updateContainerTranslate()
    },
    nextSlide() {
      this.stopAutoplay()
      this.nextSlideInternal()
    },
    prevSlide() {
      this.stopAutoplay()
      if (this.currentIndex > 0) {
        this.currentIndex--
      } else {
        this.currentIndex = this.innerDivCount - 1
      }
      this.updateContainerTranslate()
    },
    goToSlide(index) {
      this.stopAutoplay()
      this.currentIndex = index
      this.updateContainerTranslate()
    },
    getContainer() {
      return this.$refs.carouselContainer
    },
    countChildren() {
      const container = this.getContainer()
      if (container) {
        this.innerDivCount = Array.from(container.children).filter(
          (child) => child.nodeType === 1
        ).length
      }
    },
    updateContainerTranslate() {
      const container = this.getContainer()
      if (container) {
        const slideWidth = container.clientWidth / this.innerDivCount
        const offset = this.currentIndex * slideWidth
        this.containerTranslate = `-${offset}px`
      }
    },
    collectTitles() {
      const titles = []
      const container = this.getContainer()
      if (container) {
        const titleDivs = container.querySelectorAll('.c-title')
        titleDivs.forEach((div) => {
          titles.push(div.textContent.trim())
        })
      }
      this.titlesArray = titles
    },
    handleResize() {
      this.updateContainerTranslate()
    },
  },
}
</script>

<style lang="scss" scoped>
.carousel {
  display: block;
  overflow: hidden;
  position: relative;

  &__external {
    position: relative;

    .carousel__arrow {
      top: 50%;
      &:focus {
        border: 0.125rem solid white;
      }
    }

    .carousel__leftArrow {
      display: flex;
      left: 0.2rem;

      @media screen and (min-width: 425px) {
        left: 0.5rem;
        margin-left: 0.5rem;
      }

      @media screen and (min-width: 768px) {
        left: 1rem;
      }

      @media screen and (min-width: 992px) {
        left: 2rem;
        margin-left: unset;
      }
    }

    .carousel__rightArrow {
      display: flex;
      right: 0.2rem;

      @media screen and (min-width: 425px) {
        right: 0.5rem;
        margin-right: 0.5rem;
      }

      @media screen and (min-width: 768px) {
        right: 1rem;
      }

      @media screen and (min-width: 992px) {
        right: 2rem;
        margin-right: unset;
      }
    }
  }

  &__indicators {
    display: flex;
    left: 50%;
    gap: 0.5rem;
    position: absolute;
    transform: translate(-50%, -50%);
    top: 95%;
    z-index: 1;

    @media screen and (min-width: 992px) {
      gap: 1rem;
      top: 90%;
    }

    @media screen and (min-width: 1200px) {
      gap: 1.5rem;
      top: 80%;
    }
  }

  &__indicator {
    cursor: pointer;
    height: 1rem;
    width: 1rem;
    border-radius: 50%;
    border: 0.0625rem solid;
  }

  &__arrow {
    align-items: center;
    cursor: pointer;
    display: flex;
    justify-content: center;
    height: 2rem;
    position: absolute;
    width: 2rem;
    z-index: 1;

    @media screen and (min-width: 768px) {
      transform: translate(0%, -50%);
      height: 4.5rem;
      width: 4.5rem;
    }

    .carousel__arrowImage {
      width: 0.463rem;
      height: 0.75rem;

      @media screen and (min-width: 768px) {
        height: 1.5rem;
        width: 0.926rem;
      }
    }
  }

  &__container {
    display: flex;
    transition: transform 0.5s ease-in-out;
  }
}
</style>
